import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { cn } from '../../../../../../utils/utils';
import { StepperControlsComponent } from '../../../../../components/ui/stepper-controls/stepper-controls.component';
import { RoutingTabComponent } from '../../../../../components/ui/tabs/routing-tab/routing-tab.component';
import { CsvFeedbackContactsTableComponent } from '../../../../../components/tables/csv-feedback-contacts-table/csv-feedback-contacts-table.component';
import { ActivatedRoute, Router } from '@angular/router';
import { CsvServiceService } from '../../../../../services/csv/csv-service.service';
import { ContactsService } from '../../../../../services/contacts/contacts.service';
import { formatCount } from '../../../../../../utils/sms';
import { csvFileName, downloadCsv } from '../../../../../../utils/csv';

type TCurrentView = 'success' | 'fail';

@Component({
  selector: 'csv-feedback',
  standalone: true,
  imports: [StepperControlsComponent, RoutingTabComponent, CsvFeedbackContactsTableComponent],
  templateUrl: './feedback.component.html',
  styleUrls: ['../csv-step.css', './feedback.component.css'],
})
export class FeedbackComponent implements OnInit {
  router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  merge = cn;
  readonly formatCount = formatCount;

  csvService = inject(CsvServiceService);
  contactService = inject(ContactsService);

  get successfulContactsCount() {
    return this.csvService.getSuccessfulContacts.length;
  }

  get failedContactsCount() {
    return this.csvService.getFailedContacts.length;
  }

  /** Rows sent by the upload; null when the result was opened without one (old session). */
  get sentCount(): number | null {
    return this.csvService.getUploadSummary?.sent ?? null;
  }

  /**
   * Numbers already in the organisation are dropped by the server without
   * appearing in either list, so they are whatever is left over. Worked out
   * from the counts saved at upload time: deleting added contacts here
   * shrinks the Added list but does not make more rows "skipped".
   */
  get skippedCount(): number {
    const summary = this.csvService.getUploadSummary;
    return summary ? Math.max(0, summary.sent - summary.created - summary.failed) : 0;
  }

  currentView: TCurrentView = 'success';

  // tabs that mirror the contacts module's routing tabs, driven by the `view`
  // query param while keeping the feedback step (`title=feedback`) active
  get viewTabs() {
    return [
      {
        title: 'Added',
        count: this.successfulContactsCount,
        link: '/organization/contacts/csv',
        queryParams: { title: 'feedback', view: 'success' },
      },
      {
        title: 'Failed',
        count: this.failedContactsCount,
        link: '/organization/contacts/csv',
        queryParams: { title: 'feedback', view: 'fail' },
      },
    ];
  }

  ngOnInit() {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.currentView = params['view'] === 'fail' ? 'fail' : 'success';
    });
  }

  downloadFailedRows() {
    const rows = this.csvService.getFailedContacts;
    if (!rows.length) return;
    downloadCsv(csvFileName('contacts', 'failed import'), this.csvService.problemRowsCsv(rows));
  }

  /** The contact count is stale after an import; the list fetches its own page when it opens. */
  private refreshContacts() {
    this.contactService.fetchContactsCount();
  }

  done() {
    this.csvService.clearCsvData();
    this.refreshContacts();
    this.router.navigate(['organization', 'contacts']);
  }

  importAnother() {
    this.csvService.clearCsvData();
    this.refreshContacts();
    this.router.navigate(['organization', 'contacts', 'csv']);
  }
}
